import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInDays, isToday, isTomorrow } from 'date-fns';
import { BookOpen, Plus, Edit3, Trash2, Calendar, Clock } from 'lucide-react';
import { getSubjects, saveSubjects, getTasks } from '../utils/storage';

const Subjects = () => {
  const [subjects, setSubjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setSubjects(getSubjects());
    setTasks(getTasks());
  };

  const getTaskCountForSubject = (subjectId) => {
    const subjectTasks = tasks.filter(task => task.subjectId === subjectId);
    return {
      total: subjectTasks.length,
      completed: subjectTasks.filter(task => task.completed).length,
      pending: subjectTasks.filter(task => !task.completed).length
    };
  };

  const getSubjectStats = (subject) => {
    const stats = getTaskCountForSubject(subject.id);
    return stats;
  };

  const getNextDeadline = (subjectId) => {
    const subjectTasks = tasks.filter(task =>
      task.subjectId === subjectId && !task.completed
    );

    if (subjectTasks.length === 0) return null;

    // Sort by deadline (earliest first)
    const sortedTasks = subjectTasks
      .filter(task => task.deadline)
      .sort((a, b) => parseISO(a.deadline) - parseISO(b.deadline));

    if (sortedTasks.length === 0) return null;

    const nextTask = sortedTasks[0];
    const deadline = parseISO(nextTask.deadline);
    const now = new Date();

    let deadlineText = '';
    if (isToday(deadline)) {
      deadlineText = 'Today';
    } else if (isTomorrow(deadline)) {
      deadlineText = 'Tomorrow';
    } else {
      const daysDiff = differenceInDays(deadline, now);
      if (daysDiff <= 7) {
        deadlineText = format(deadline, 'EEEE'); // Day name
      } else {
        deadlineText = format(deadline, 'MMM dd');
      }
    }

    return {
      text: deadlineText,
      taskTitle: nextTask.title,
      isUrgent: isToday(deadline) || differenceInDays(deadline, now) <= 2
    };
  };

  const deleteSubject = (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject? All associated tasks will remain but may show as "Unknown" subject.')) {
      const updatedSubjects = subjects.filter(subj => subj.id !== subjectId);
      saveSubjects(updatedSubjects);
      setSubjects(updatedSubjects);
    }
  };

  return (
    <div className="container">
      <div className="header-section">
        <h1>Subjects</h1>
        <p className="form-subtitle">Organize your tasks by subject and track your progress</p>
      </div>

      {subjects.length > 0 ? (
        <div className="subjects-grid">
          {subjects.map(subj => {
            const stats = getSubjectStats(subj);
            const nextDeadline = getNextDeadline(subj.id);
            return (
              <div
                key={subj.id}
                className="subject-card"
                style={{
                  '--subject-color': subj.color || '#64748b'
                }}
              >
                <div className="subject-header">
                  <div
                    className="subject-color-dot"
                    style={{ backgroundColor: subj.color || '#64748b' }}
                  />
                  <h3 className="subject-name">{subj.name}</h3>
                  <div className="subject-actions">
                    <button
                      className="subject-action-btn edit"
                      onClick={() => navigate('/add-subject', { state: { subject: subj } })}
                      aria-label={`Edit ${subj.name}`}
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      className="subject-action-btn delete"
                      onClick={() => deleteSubject(subj.id)}
                      aria-label={`Delete ${subj.name}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {nextDeadline && (
                  <div className="next-deadline">
                    <Calendar size={14} />
                    <div className="deadline-info">
                      <span className={`deadline-text ${nextDeadline.isUrgent ? 'urgent' : ''}`}>
                        Due {nextDeadline.text}
                      </span>
                      <span className="deadline-task">{nextDeadline.taskTitle}</span>
                    </div>
                  </div>
                )}

                <div className="subject-stats">
                  <div className="stat-item">
                    <span className="stat-number">{stats.total}</span>
                    <span className="stat-label">Total Tasks</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number completed">{stats.completed}</span>
                    <span className="stat-label">Completed</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number pending">{stats.pending}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                </div>

                <div className="subject-progress">
                  {stats.total > 0 && (
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${(stats.completed / stats.total) * 100}%`,
                          background: `linear-gradient(90deg, ${subj.color || '#64748b'} 0%, ${subj.color || '#64748b'}dd 100%)`
                        }}
                      />
                    </div>
                  )}
                  <span className="progress-text">
                    {stats.total > 0
                      ? `${Math.round((stats.completed / stats.total) * 100)}% Complete`
                      : 'No tasks yet'
                    }
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-illustration">
            <BookOpen size={64} strokeWidth={1} />
          </div>
          <h2>No subjects yet</h2>
          <p>Create subjects to better organize your tasks and track progress by topic.</p>
          <Link to="/add-subject">
            <button className="add-button">
              <Plus size={20} />
              Add Your First Subject
            </button>
          </Link>
        </div>
      )}

      {subjects.length > 0 && (
        <div className="add-subject-section">
          <Link to="/add-subject">
            <button className="add-button secondary">
              Add Another Subject
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default Subjects;
